// ============================================================================
// Adam Platform — Releases Cockpit (BUCKET I.8)
// ============================================================================
// The orchestrator cockpit for fleet-wide upgrades. Replaces the prior
// "stocktake surface" view. Composes four surfaces:
//
//   1. PreflightModal     — operator dry-runs the upgrade across the
//                           target cohort before anything fires.
//   2. StagedUpgradeProgress — stage-by-stage progress of the active
//                           multi-stage canary, falling back to batch
//                           copy when the server can't segment cleanly.
//   3. ActiveRollbackBanner  — prominent banner when auto_rollback has
//                           fired.
//   4. Promote / Abort controls — real abort writes audit+marks task
//                           aborted; promote is a stub with a tooltip
//                           explaining the I.8 follow-up.
//
// Principles:
//   * P4 — every surface has a truthful fallback (no fake stages,
//     no optimistic rollback banner, no phantom promote success copy).
//   * P11 — the component scope is first-class in every surface that
//     touches it (preflight form, progress header, rollback banner).
// ============================================================================
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  PageHeader,
  StatCard,
  Card,
  SectionHeader,
  Badge,
  Spinner,
  Button,
  Modal,
  QueryError,
} from "../components";
import { apiGet, apiPost } from "../lib/api";
import { usePolling } from "../hooks/usePolling";
import PreflightModal from "../components/PreflightModal";
import StagedUpgradeProgress from "../components/StagedUpgradeProgress";
import ActiveRollbackBanner from "../components/ActiveRollbackBanner";
// Bucket C Round 2 — canonical fleet upgrade wire shape. Type-only so Vite
// never resolves zod at runtime.
import type { FleetUpgradeView } from "../../../../src/shared/contracts/upgrade-task.js";

interface Version {
  id: string;
  imageTag: string;
  isStable: boolean;
}

interface VersionDistribution {
  imageTag: string;
  tenantCount: number;
}

export default function Releases() {
  const queryClient = useQueryClient();
  const [preflightOpen, setPreflightOpen] = useState(false);
  const [abortModalOpen, setAbortModalOpen] = useState(false);

  const {
    data: versionsData,
    isLoading: loadingVersions,
    error: versionsError,
  } = useQuery({
    queryKey: ["versions"],
    queryFn: () => apiGet<{ versions: Version[]; total: number }>("/api/versions"),
  });

  const { data: distData, isLoading: loadingDistribution } = useQuery({
    queryKey: ["fleet-versions"],
    queryFn: () => apiGet<{ distribution: VersionDistribution[] }>("/api/fleet/versions"),
  });

  const { data: activeUpgrade } = useQuery<FleetUpgradeView | null>({
    queryKey: ["fleet-upgrade-active"],
    queryFn: () => apiGet<FleetUpgradeView | null>("/api/fleet/upgrade/active"),
  });

  usePolling([["versions"], ["fleet-versions"], ["fleet-upgrade-active"]], 30_000);

  const refetchActive = () => queryClient.invalidateQueries({ queryKey: ["fleet-upgrade-active"] });

  const promoteMutation = useMutation({
    mutationFn: async (id: string) => apiPost(`/api/fleet/upgrade/${id}/promote`),
    onSuccess: refetchActive,
  });

  const abortMutation = useMutation({
    mutationFn: async (id: string) => apiPost(`/api/fleet/upgrade/${id}/abort`),
    onSuccess: () => {
      setAbortModalOpen(false);
      refetchActive();
    },
  });

  const versions = versionsData?.versions ?? [];
  const stableVersion = versions.find((v) => v.isStable)?.imageTag ?? "none";
  const distribution = distData?.distribution ?? [];
  const totalDeployed = distribution.reduce((sum, item) => sum + item.tenantCount, 0);

  if ((loadingVersions || loadingDistribution) && !versionsData && !distData) {
    return (
      <div>
        <PageHeader title="Fleet Upgrades" subtitle="Upgrade orchestrator cockpit" />
        <div className="flex items-center justify-center py-20">
          <Spinner size={24} />
        </div>
      </div>
    );
  }

  if (versionsError && !versionsData) {
    return (
      <div>
        <PageHeader title="Fleet Upgrades" subtitle="Upgrade orchestrator cockpit" />
        <QueryError error={versionsError} queryKeys={[["versions"], ["fleet-versions"]]} />
      </div>
    );
  }

  const isInRollback = activeUpgrade?.status === "auto_rollback" && !!activeUpgrade.rollback;
  const isInProgress = activeUpgrade?.status === "in_progress";
  // Promote is only visible when there are more stages left AND the current
  // stage is healthy. We don't have a "current stage index" explicitly — use
  // the last healthy stage as a proxy (if the upgrade is running, the
  // next unhealthy/pending one is what promote would advance).
  const hasNextStage = (activeUpgrade?.stages ?? []).some((s) => s.status === "pending");
  const currentStageHealthy = (activeUpgrade?.stages ?? []).some((s) => s.status === "healthy");
  const showPromote = isInProgress && hasNextStage && currentStageHealthy;

  return (
    <div>
      <PageHeader
        title="Fleet Upgrades"
        subtitle="Upgrade orchestrator cockpit — preflight, promote, and monitor staged rollouts"
        actions={
          <Button
            data-testid="open-preflight-btn"
            variant="primary"
            onClick={() => setPreflightOpen(true)}
          >
            Upgrade fleet
          </Button>
        }
      />

      {isInRollback && activeUpgrade?.rollback && (
        <ActiveRollbackBanner rollback={activeUpgrade.rollback} />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <StatCard title="Stable Version" value={stableVersion} />
        <StatCard title="Versions in Fleet" value={String(distribution.length)} />
        <StatCard
          title="Fleet Size"
          value={`${totalDeployed} deployment${totalDeployed === 1 ? "" : "s"}`}
        />
      </div>

      {activeUpgrade && (
        <>
          <StagedUpgradeProgress upgrade={activeUpgrade} />

          <Card className="mb-6">
            <SectionHeader
              title="Controls"
              subtitle="Promote the canary to the next stage, or abort the upgrade"
            />
            <div className="flex items-center gap-3 text-xs">
              {showPromote && (
                <Button
                  data-testid="promote-btn"
                  variant="primary"
                  disabled={promoteMutation.isPending}
                  onClick={() => promoteMutation.mutate(activeUpgrade.id)}
                  title="Promote-to-next-stage is not yet wired through the in-memory multi-stage runner. The operator intent will be recorded in the audit log for follow-up."
                >
                  {promoteMutation.isPending ? (
                    <span className="inline-flex items-center gap-2">
                      <Spinner size={14} />
                      Promoting…
                    </span>
                  ) : (
                    "Promote to next stage"
                  )}
                </Button>
              )}
              {isInProgress && (
                <Button
                  data-testid="abort-btn"
                  variant="danger"
                  disabled={abortMutation.isPending}
                  onClick={() => setAbortModalOpen(true)}
                >
                  Abort upgrade
                </Button>
              )}
              <Badge color={isInRollback ? "red" : isInProgress ? "amber" : "green"}>
                {activeUpgrade.status}
              </Badge>
            </div>
            {promoteMutation.isSuccess && (
              <div
                className="mt-2 text-xs"
                style={{ color: "var(--c-text-faint)" }}
                data-testid="promote-note"
              >
                Promote request recorded. Note: precise stage promotion is a BUCKET I.8 follow-up —
                see audit log.
              </div>
            )}
          </Card>
        </>
      )}

      {!activeUpgrade && (
        <Card>
          <div className="text-sm" style={{ color: "var(--c-text-secondary)" }}>
            No upgrade is currently running. Click{" "}
            <strong style={{ color: "var(--c-text)" }}>Upgrade fleet</strong> to preflight a target
            version.
          </div>
        </Card>
      )}

      {/* Preflight modal (controls its own form + table state) */}
      <PreflightModal
        open={preflightOpen}
        onClose={() => setPreflightOpen(false)}
        onUpgradeStarted={() => {
          setPreflightOpen(false);
          refetchActive();
        }}
      />

      {/* Abort confirmation modal */}
      {activeUpgrade && (
        <Modal
          open={abortModalOpen}
          onClose={() => setAbortModalOpen(false)}
          title="Abort upgrade?"
          size="md"
        >
          <div className="space-y-3 text-sm" style={{ color: "var(--c-text-secondary)" }}>
            <p>
              Abort the upgrade <code>{activeUpgrade.fromVersion}</code> →{" "}
              <code>{activeUpgrade.toVersion}</code>?
            </p>
            <p>
              Already-upgraded tenants will remain on the target version. New stages will not fire.
            </p>
            <div className="flex items-center justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setAbortModalOpen(false)}>
                Cancel
              </Button>
              <Button
                data-testid="abort-confirm-btn"
                variant="danger"
                disabled={abortMutation.isPending}
                onClick={() => abortMutation.mutate(activeUpgrade.id)}
              >
                {abortMutation.isPending ? "Aborting…" : "Confirm abort"}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
